import React, {useContext, useEffect, useRef, useState} from 'react';
import {Template} from "../../../../components/template/template.jsx";
import Tabs from "./components/tabs.jsx";
import ProfilePicture from "./components/profile-picture.jsx";
import General from "./components/tabs/general.jsx";
import {Card} from "../../../../components/card/card.jsx";
import Button from "../../../../components/button/button.jsx";
import Characteristics from "./components/tabs/characteristics.jsx";
import {IndividualContext} from "./individual-context.jsx";
import Addresses from "./components/tabs/addresses.jsx";
import OtherInformation from "./components/tabs/other-information.jsx";
import Articles from "./components/tabs/articles.jsx";
import Phones from "./components/tabs/phones.jsx";
import Attachments from "./components/tabs/attachments.jsx";
import Occurrences from "./components/tabs/occurrences.jsx";
import IndividualResume from "./components/tabs/individual-resume.jsx";
import projectUtils from "../../../../utils/project-utils.js";
import individualService from "../../../../services/web/individual-service.js";
import AlertMessages from "../../../../components/messages/alert-message.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import Sos from "./components/tabs/sos.jsx";
import Bos from "./components/tabs/bos.jsx";
import Encounters from "./components/tabs/encounters.jsx";
import IndividualHeaderActions from "./components/individual-header-actions.jsx";
import ActivityMap from "./components/tabs/activity-map.jsx";
import Photos from "./components/tabs/photos.jsx";
import {useInactiveTabAutofillProtection} from "../../../../hooks/use-required-tab-errors.js";

const IndividualForm = () => {
    const navigate = navigateHook();
    const {individual, cpf, individualInfo, attachList, attachesDescription, faceFile, refetch, changes, setChanges, setFaceFile} = useContext(IndividualContext)
    const [tabSelected, setTabSelected] = useState("general")
    const [headerSelected, setHeaderSelected] = useState(-1);
    const [isLoading, setIsLoading] = useState(false)
    const printRef = useRef(undefined);
    const tabsRef = useRef(null);

    useInactiveTabAutofillProtection(tabsRef, tabSelected);

    const tabComponents = [
        {
            key: "resume",
            component: (
                <IndividualResume
                    headerSelected={headerSelected}
                    printRef={printRef}
                    attachesDescription={attachesDescription}
                />
            ),
        },
        {key: "general", component: <General/>},
        {key: "characteristics", component: <Characteristics/>},
        {key: "addresses", component: <Addresses/>},
        {key: "other-information", component: <OtherInformation/>},
        {key: "articles", component: <Articles/>},
        {key: "phones", component: <Phones/>},
        {key: "attachments", component: <Attachments/>},
        {key: "photos", component: <Photos/>},
        {key: "occurrences", component: <Occurrences/>},
        {key: "sos", component: <Sos/>},
        {key: "bos", component: <Bos/>},
        {key: "encounters", component: <Encounters/>},
        {key: "activity-map", component: <ActivityMap/>},
    ];

    const save = () => {
        setIsLoading(true)
        const attachesDescriptionList = Object.entries(attachesDescription)
            .map(([key, value]) => {
                const attachFind = attachList.find(attach => attach.attachId === key);
                return {fileName: attachFind?.name, description: value, attachId: key}
            });
        const articleAttachList = (individual.articles ?? []).filter(article => article.articleFile instanceof File);
        const individualData = {
            ...individual,
            articles: (individual.articles ?? []).map(({articleFile, ...article}) => article)
        };

        const formData = new FormData();
        formData.append("data", JSON.stringify(individualData));
        formData.append("attachDescription", JSON.stringify(attachesDescriptionList));
        formData.append("oldCpf", cpf);
        formData.append("faceFile", faceFile);

        for (const attach of attachList) {
            if (attach.new) formData.append("files", attach)
        }

        for (const article of articleAttachList) {
            formData.append(`files`, article.articleFile);
        }

        const response = cpf ? individualService.update(formData) : individualService.create(formData);
        response.then(({data}) => {
                 projectUtils.success(!cpf);
                if (cpf && data.toString() === cpf) {
                    refetch()
                    return
                }

            const urlDev = `/individuals/form/${data}`;
            navigate(urlDev);
        })
        .catch((error) => {
            console.error(error);

            const {message} = error.response.data;

            const errMessage = AlertMessages[message] ?? "Não foi possível salvar.";
            projectUtils.swalErrorMessage("", errMessage, "Ok");
        })
        .finally(() => {
            setIsLoading(false)
            setFaceFile(null)
        })
    }

    const destroy = () => {
        projectUtils.swalMessage(
            "warning",
            "Excluir",
            `Excluir o indivíduo "${individual?.name ?? ""}"?`,
            "Sim",
            true,
            "Não",
            ({isConfirmed}) => {
                if (!isConfirmed) return;

                setIsLoading(true);
                individualService.delete(cpf)
                    .then(() => {
                        projectUtils.success(true, "Excluído com sucesso");
                        navigate("/individuals");
                    })
                    .catch((error) => {
                        console.error(error);

                        const {message} = error.response.data;
                        const errMessage = AlertMessages[message] ?? "Não foi possível excluir.";

                        projectUtils.swalErrorMessage("", errMessage, "Ok");
                    })
                    .finally(() => setIsLoading(false));
            }
        );
    }

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title browserTitle={individual.name ?? "Sem nome"}>Indivíduo</Template.Title>
                <IndividualHeaderActions
                    tabSelected={tabSelected}
                    headerSelected={headerSelected}
                    setHeaderSelected={setHeaderSelected}
                    printRef={printRef}
                />
            </Template.Header>

            <Template.Body>
                <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-2 flex flex-col h-[calc(100vh-69px)]">
                        <ProfilePicture />
                        <Tabs tabSelected={tabSelected} setTabSelected={setTabSelected}/>
                    </div>
                    <div className="col-span-10 relative">
                        <div ref={tabsRef} className={`${tabSelected === "resume" ? "h-[calc(100vh-69px)]" : "h-[calc(100vh-131px)]"} overflow-auto`}>
                            {tabComponents.map(({key, component}) => (
                                <div
                                    key={key}
                                    data-required-tab={key}
                                    hidden={tabSelected !== key}
                                    aria-hidden={tabSelected !== key}
                                >
                                    {component}
                                </div>
                            ))}
                        </div>

                        {
                            tabSelected !== "resume" &&
                            <Card className={"mt-2"}>
                                <Card.Body>
                                    <div className={"flex justify-between items-center gap-2"}>
                                        <p className={"report-center"}>
                                            {
                                                individualInfo?.createdAt &&
                                                <span>Cadastrado por <b>{individualInfo.userCreated}</b> em <b>{projectUtils.formatDate(individualInfo.createdAt)}</b> {individualInfo?.updatedAt && <>e</>}</span>
                                            }

                                            {
                                                (individualInfo?.updatedAt && individualInfo?.userUpdated) &&
                                                <span> Editado por <b>{individualInfo.userUpdated}</b> em <b>{projectUtils.formatDate(individualInfo.updatedAt)}</b></span>
                                            }
                                        </p>
                                        <div className={"flex items-center gap-2"}>
                                            {
                                                cpf &&
                                                <Button target={"danger"} onClick={destroy} actionFeedbackType='delete'>
                                                    Excluir
                                                </Button>
                                            }

                                            <Button onClick={save} disabled={changes.length === 0} actionFeedbackType='save' onActionFeedbackSuccess={() => setChanges([])}>
                                                Salvar
                                            </Button>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        }
                    </div>
                </div>
            </Template.Body>
        </Template>
    );
};

export default IndividualForm;